import type { MouseEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth as useAuthContext } from '@/components/auth/AuthProvider'
import { getCollectionIcon } from '@/components/shared/custom-fields/CollectionIconPicker'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { type NavItem } from '@/config/navigation'
import type { RailAreaDef, RailAreaKey } from '@/config/rail-areas'
import {
  AREA_ITEM_ORDER,
  getRailAreaForNavItem,
  NAVIGATION_STYLE,
  RAIL_AREAS,
  SECTION_TO_AREA,
} from '@/config/rail-areas'
import { useLiveChatUnreadCount } from '@/hooks/queries/useLiveChatUnreadCount'
import { useUnreadThreadsCount } from '@/hooks/queries/useThreadsQuery'
import { useUnreadTicketsCount } from '@/hooks/queries/useTicketUnreadCount'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccountType } from '@/hooks/useAccountType'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { usePermissions } from '@/hooks/usePermissions'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { customCollectionsApi } from '@/lib/api/custom-collections.api'
import { createLogger } from '@/lib/logger'
import { prefetchModuleForPath } from '@/lib/prefetch/modulePrefetch'
import { useThreadListRealtime } from '@/lib/realtime/features/useThreadListRealtime'
import { cn } from '@/lib/utils'
import {
  isPopupWindow,
  isTauri,
  openFocusWindow,
  openInNewWindow,
} from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { getOrgPath } from '@/utils/org-paths'
import {
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  BookOpenTextIcon,
  CaretDownIcon,
  DotsThreeIcon,
  EyeIcon,
  EyeSlashIcon,
  GearIcon,
  GearSixIcon,
  PushPinIcon,
  PushPinSlashIcon,
  SidebarSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import type { FeatureFlagKeyType, PinnedNavRouteItem } from '@scrambl/shared'
import {
  ModuleTypeEnum,
  PermissionActionEnum,
  ThreadTypeEnum,
} from '@scrambl/shared'

import { Header } from './Header'

const logger = createLogger('components/layout/AppSidebar')

const SUBSCRIPTION_NAV_BADGES: Record<string, string> = {
  analytics: 'Business',
}

interface AppSidebarProps {
  children: ReactNode
}

// Optimized sidebar trigger with reduced animation
function CustomSidebarTrigger({ className }: { className?: string }) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(state => state.setSidebarOpen)
  const isAdminMode = useUIPreferencesStore(state => state.isAdminMode)
  const isMobile = useIsMobile()

  // In rail builds the desktop navigation is a fixed hover rail with no
  // expanded state to toggle, so the trigger only renders on mobile (opens
  // the sheet) and in admin mode. Home Base builds keep it: it toggles
  // between the full sidebar and the compact icon rail.
  if (!isMobile && !isAdminMode && NAVIGATION_STYLE === 'rail') {
    return null
  }

  const handleToggle = () => {
    toggleSidebar()
    // Direct state update without timeout
    setSidebarOpen(state === 'collapsed')
  }

  return (
    <Button
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7', className)}
      onClick={handleToggle}
    >
      <SidebarSimpleIcon weight="bold" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

export function AppSidebar({ children }: AppSidebarProps) {
  const location = useLocation()
  const { user } = useAuthContext()
  const { state, setOpenMobile } = useSidebar()
  const { checkPermission, isLoadingPermissions } = usePermissions()
  const isAdminMode = useUIPreferencesStore(state => state.isAdminMode)
  const setAdminMode = useUIPreferencesStore(state => state.setAdminMode)
  const collapsedNavGroups = useUIPreferencesStore(
    state => state.collapsedNavGroups,
  )
  const setNavGroupCollapsed = useUIPreferencesStore(
    state => state.setNavGroupCollapsed,
  )
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme)
  const tauriGlassEnabled = useUIPreferencesStore(
    state => state.tauriGlassEnabled,
  )
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  // Compute admin status directly from organization to ensure reactivity
  const membershipRole = currentOrganization?.userMembership?.role
  const isExternalAccess = currentOrganization?.isExternalAccess === true
  const preloadProfileImages = useUserProfileStore(
    state => state.preloadProfileImages,
  )
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  // "More" overflow expansion, tracked per rail area.
  const [expandedMoreAreas, setExpandedMoreAreas] = useState<Set<string>>(
    new Set(),
  )
  // Rail area the user explicitly selected (mobile panel only — desktop uses
  // the hover flyout). Cleared on navigation so the route-derived area takes
  // over again (the panel follows where you are).
  const [selectedArea, setSelectedArea] = useState<RailAreaKey | null>(null)
  // Desktop hover flyout: which area's menu is currently flown out
  // ('pinned' = the Pinned shortcuts flyout). A short close delay bridges
  // the pointer moving between the rail and the flyout.
  const [hoverArea, setHoverArea] = useState<RailAreaKey | 'pinned' | null>(
    null,
  )
  const hoverCloseTimer = useRef<number | null>(null)
  // A row's "…" dropdown renders in a portal outside the flyout, so pointer
  // travel into it looks like leaving the flyout. While any row menu is
  // open, hold the flyout open.
  const rowMenuOpenRef = useRef(false)
  const { theme } = useTheme()
  const organizationId = currentOrganization?.id || null
  const { checkFeature, isLoading: isLoadingSubscriptionFeatures } =
    useSubscriptionFeatures()

  // Get pinned collections for sidebar (must be before isLinkActive which uses it)
  const {
    preferences: navPreferences,
    setNavItemHidden,
    pinNavItem,
    unpinNavItem,
    isNavRoutePinned,
  } = useNavigationPreferences()

  // Optimistic per-user nav visibility overrides. Keyed by item href. This
  // mirrors what gets persisted to the navigation preference, but updating it
  // is a local state change so the sidebar re-renders the show/hide split
  // immediately on click — without waiting for the save round-trip or relying
  // on the derived-preference re-render to propagate.
  const [pendingNavVisibility, setPendingNavVisibility] = useState<
    Record<string, boolean>
  >({})

  // Effective visibility map: persisted preference values with any optimistic
  // (just-clicked) overrides layered on top.
  const effectiveHiddenNavItems = useMemo(
    () => ({
      ...(navPreferences.hiddenNavItems ?? {}),
      ...pendingNavVisibility,
    }),
    [navPreferences.hiddenNavItems, pendingNavVisibility],
  )

  // Resolve effective visibility for a nav item: an override (optimistic or
  // persisted) wins, otherwise the item's built-in default applies.
  const isNavItemHidden = useCallback(
    (href: string, defaultHidden = false): boolean => {
      const override = effectiveHiddenNavItems[href]
      return typeof override === 'boolean' ? override : defaultHidden
    },
    [effectiveHiddenNavItems],
  )

  const hasCollectionsFeature = checkFeature('collections')
  const pinnedCollectionAccessQueries = useQueries({
    queries: (navPreferences.pinnedCollections ?? []).map(collection => ({
      queryKey: [
        'custom-collections',
        'sidebar-access',
        organizationId,
        collection.collectionId,
      ],
      queryFn: () =>
        customCollectionsApi.getCollection(collection.collectionId),
      enabled: !!organizationId && hasCollectionsFeature,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  })
  const accessibleCollectionIds = new Set(
    pinnedCollectionAccessQueries
      .filter(query => query.isSuccess)
      .map(query => query.data.id),
  )
  const accessiblePinnedCollections = (() => {
    if (!navPreferences.pinnedCollections?.length) return []
    return navPreferences.pinnedCollections.filter(item =>
      accessibleCollectionIds.has(item.collectionId),
    )
  })()

  useThreadListRealtime(organizationId, { suppressLogging: true })

  // Determine if we're in dark mode (for system theme, check the actual applied theme)
  const isDarkMode = (() => {
    if (theme === 'dark') return true
    if (theme === 'light') return false
    // For system theme, check what's actually applied
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    )
  })()

  // Get the current theme configuration
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]

  // In dark mode, all themes use light text (dark backgrounds)
  // In light mode, use the theme's specified light text color
  const useDarkText = isDarkMode ? false : themeConfig.lightTextColor === 'dark'

  // Memoize expensive calculations - include all relevant state to ensure it updates when org changes
  const hasAdminPermissions = (() => {
    // Return false if no organization is set to prevent premature admin checks
    if (!currentOrganization) {
      return false
    }
    // External org users cannot manage the org (they use the host's permission set for other features)
    if (isExternalAccess) {
      return false
    }
    // Check if user is admin/owner via membership
    return membershipRole === 'ADMIN' || membershipRole === 'OWNER'
  })()
  const isIntegrationsAdminRoute = useMemo(
    () => location.pathname.includes('/admin/integrations'),
    [location.pathname],
  )
  const canAccessIntegrationsAdminRoute =
    isIntegrationsAdminRoute &&
    (isLoadingPermissions ||
      checkPermission(ModuleTypeEnum.INTEGRATION, PermissionActionEnum.READ))
  const hasAdminModeAccess =
    hasAdminPermissions || canAccessIntegrationsAdminRoute
  const isSidebarCollapsed = state === 'collapsed'
  // Home Base style: full-width sidebar with pinned items + collapsible
  // groups (desktop only — the mobile sheet keeps its rail + panel). Set
  // system-wide via NAVIGATION_STYLE in config/rail-areas.ts. When the user
  // collapses the sidebar, Home Base degrades to the icon rail — sub items
  // stay reachable through the hover popovers instead of cramming the tree
  // into icon width.
  const isHomeBase =
    NAVIGATION_STYLE === 'homeBase' && !isMobile && !isSidebarCollapsed
  const isRailMode = !isMobile && !isAdminMode && !isHomeBase

  // Monitor organization context changes - triggers re-render when org changes
  useEffect(() => {
    // Effect triggers when org or admin permissions change
  }, [currentOrganization, hasAdminPermissions])

  // Auto-exit admin mode if user loses admin permissions
  useEffect(() => {
    if (isAdminMode && !hasAdminModeAccess && currentOrganization) {
      setAdminMode(false)
      navigate(`/${currentOrganization.slug}/today`)
    }
  }, [
    isAdminMode,
    hasAdminModeAccess,
    setAdminMode,
    navigate,
    currentOrganization,
  ])

  // Simplified profile image loading - use preload method
  useEffect(() => {
    if (user?.id) {
      const timer = setTimeout(() => {
        preloadProfileImages([user.id])
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [user?.id, preloadProfileImages])

  // The panel follows navigation: once the route changes, drop any explicit
  // rail selection so the route-derived area drives the panel again.
  useEffect(() => {
    setSelectedArea(null)
  }, [location.pathname])

  const cancelFlyoutClose = () => {
    if (hoverCloseTimer.current !== null) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
  }

  const openFlyout = (areaKey: RailAreaKey | 'pinned') => {
    if (isMobile) return
    cancelFlyoutClose()
    setHoverArea(areaKey)
  }

  const scheduleFlyoutClose = () => {
    if (isMobile) return
    if (rowMenuOpenRef.current) return
    cancelFlyoutClose()
    hoverCloseTimer.current = window.setTimeout(() => {
      setHoverArea(null)
      hoverCloseTimer.current = null
    }, 180)
  }

  // Clear any pending close timer on unmount
  useEffect(() => cancelFlyoutClose, [])

  const handleNavigate = () => {
    cancelFlyoutClose()
    setHoverArea(null)
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handleCloseMobile = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const toggleAdminMode = () => {
    if (!hasAdminPermissions) {
      logger.warn('User does not have admin permissions')
      return
    }

    if (isMobile) {
      setOpenMobile(false)
    }

    const newAdminMode = !isAdminMode
    setAdminMode(newAdminMode)

    if (newAdminMode) {
      if (currentOrganization?.slug) {
        navigate(`/${currentOrganization.slug}/admin/settings`)
      }
    } else {
      if (currentOrganization?.slug) {
        navigate(`/${currentOrganization.slug}/today`)
      } else {
        navigate('/today')
      }
    }
  }

  // Memoized link active state
  const isLinkActive = useCallback(
    (path: string) => {
      if (path.includes('/admin/')) {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}${path}` ||
            location.pathname.endsWith(path)
          )
        }
        return location.pathname.endsWith(path)
      }

      if (path === '/help') {
        return (
          location.pathname === '/help' ||
          location.pathname.startsWith('/help/')
        )
      }

      if (path === '/today') {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}/today` ||
            location.pathname === `/${currentOrganization.slug}`
          )
        }
        return location.pathname === '/today'
      }

      // My Work and Workspace Tasks are separate navigation contexts even
      // though they share the underlying task module and route tree.
      if (path === '/tasks/mine') {
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, path)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === path || location.pathname.startsWith(`${path}/`)
        )
      }

      if (path === '/tasks') {
        const orgPath = currentOrganization?.slug
          ? getOrgPath(currentOrganization.slug, path)
          : path
        const minePath = `${orgPath}/mine`
        return (
          (location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)) &&
          location.pathname !== minePath &&
          !location.pathname.startsWith(`${minePath}/`)
        )
      }

      // Notes still uses personal/workspace lenses inside one shared module.
      if (path === '/notes/mine') {
        const modulePath = '/notes'
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, modulePath)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === modulePath ||
          location.pathname.startsWith(`${modulePath}/`)
        )
      }

      // For the generic /collections path, don't highlight if we're on a pinned collection
      if (path === '/collections') {
        const pinnedSlugs = accessiblePinnedCollections.map(c => c.slug)
        const currentPathSegments = location.pathname.split('/')
        // Check if current path is /org/collections/slug where slug is a pinned collection
        if (
          currentPathSegments.length >= 4 &&
          currentPathSegments[2] === 'collections'
        ) {
          const currentSlug = currentPathSegments[3]
          if (pinnedSlugs.includes(currentSlug)) {
            return false // Don't highlight Collections when on a pinned collection page
          }
        }
      }

      if (currentOrganization?.slug) {
        const orgPath = getOrgPath(currentOrganization.slug, path)
        return (
          location.pathname === orgPath ||
          location.pathname.startsWith(`${orgPath}/`)
        )
      }

      return (
        location.pathname === path ||
        (path !== '/' && location.pathname.startsWith(path))
      )
    },
    [location.pathname, currentOrganization, accessiblePinnedCollections],
  )

  // Memoized navigation path
  const getNavigationPath = useCallback(
    (path: string) => {
      const globalRoutes = ['/organizations', '/profile', '/account', '/help']

      // Check exact matches for global routes
      if (globalRoutes.includes(path)) {
        return path
      }

      // User settings routes are global (not org-specific), except integrations which are org-scoped
      if (
        path.startsWith('/settings') &&
        !path.startsWith('/settings/integrations')
      ) {
        return path
      }

      if (path.startsWith('/admin/')) {
        if (currentOrganization?.slug) {
          return `/${currentOrganization.slug}${path}`
        }
        return path
      }

      if (currentOrganization?.slug) {
        return getOrgPath(currentOrganization.slug, path)
      }

      if (path === '/today') {
        return '/today'
      }

      return path
    },
    [currentOrganization],
  )

  // Get navigation items
  const { mainNavItems, adminNavItems } = useNavigationItems()
  const featureKeys = useMemo(
    () =>
      mainNavItems
        .map(item => item.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [mainNavItems],
  )
  const { checkFeatureFlag } = useFeatureFlags(featureKeys)
  const { accountType, isPersonal } = useAccountType()

  // Subscription-gated modules remain discoverable on free accounts, but keep
  // them under "More" by default so the primary navigation stays focused on
  // modules included with the current plan. A saved per-user visibility choice
  // still wins through isNavItemHidden.
  const isSubscriptionNavItemLocked = useCallback(
    (item: NavItem) => {
      if (!item.subscriptionFeature || isLoadingSubscriptionFeatures) {
        return false
      }
      return !checkFeature(item.subscriptionFeature)
    },
    [checkFeature, isLoadingSubscriptionFeatures],
  )

  // Determine if we should show loading skeleton for navigation
  // Show skeleton when:
  // 1. Organization exists but userMembership hasn't loaded yet (and it's not external access), OR
  // 2. User is not admin/owner AND permissions are still loading
  // Note: membershipRole and isExternalAccess are already defined at the top of the component
  const isAdminOrOwner =
    membershipRole === 'OWNER' || membershipRole === 'ADMIN'
  // For external org access, we don't have userMembership - permissions come from the external org relationship
  // So we skip the userMembership check for external access
  const isNavLoading =
    (currentOrganization?.id &&
      !currentOrganization?.userMembership?.id &&
      !isExternalAccess) ||
    (!isAdminOrOwner && !isExternalAccess && isLoadingPermissions)

  // Get unread threads count for badge
  const { data: unreadThreadsCount } = useUnreadThreadsCount({
    threadType: ThreadTypeEnum.DISCUSSION,
  })

  // Unread tickets count for the Tickets nav badge
  const { data: unreadTicketsCount } = useUnreadTicketsCount()
  const { data: unreadLiveChatCount } = useLiveChatUnreadCount({
    enabled: checkFeatureFlag('LIVE_CHAT'),
  })

  // Check if we should show focus window buttons (desktop only, not in popup mode)
  const showFocusWindowButton = isTauri() && !isPopupWindow() && !isMobile
  const isDesktopTauri = isTauri() && !isMobile

  // Handle opening a nav item in a focus window
  const handleOpenFocusWindow = useCallback(
    async (e: MouseEvent, path: string, label: string) => {
      e.preventDefault()
      e.stopPropagation()
      const fullPath = getNavigationPath(path)
      await openFocusWindow(fullPath, label)
    },
    [getNavigationPath],
  )

  const handleOpenHelpCenter = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (isMobile) {
        setOpenMobile(false)
      }

      if (isTauri() && !isMobile) {
        await openInNewWindow('/help', 'Help Center')
        return
      }

      navigate('/help')
    },
    [isMobile, navigate, setOpenMobile],
  )

  // Warm the route's JS chunk on hover intent. Pure dynamic import — no API
  // traffic — so it's safe on web too, not just desktop.
  const handlePrefetch = (path: string) => {
    if (isPopupWindow()) return
    void prefetchModuleForPath(path)
  }

  // Toggle a single nav item between "always visible" and "under More" for the
  // current user. Updates optimistic local state for an instant UI change, then
  // persists to the org-scoped USER navigation preference. Reverts on failure.
  const handleToggleNavVisibility = useCallback(
    (e: MouseEvent, href: string, nextHidden: boolean) => {
      e.preventDefault()
      e.stopPropagation()
      setPendingNavVisibility(prev => ({ ...prev, [href]: nextHidden }))
      void setNavItemHidden(href, nextHidden).catch(error => {
        logger.error('Failed to save nav visibility, reverting', error)
        setPendingNavVisibility(prev => ({ ...prev, [href]: !nextHidden }))
      })
    },
    [setNavItemHidden],
  )

  // Memoized filtered navigation items
  const filteredNavItems = (() => {
    return mainNavItems.filter(item => {
      // Account-type gate for surfaces feature flags can't express. Absent =
      // visible to all account types.
      if (item.accountTypes && !item.accountTypes.includes(accountType)) {
        return false
      }

      // Feature flag is the org-level toggle and matches what the route guard checks.
      // If an admin explicitly enables the flag, the route is already reachable, so the
      // sidebar should show the item too — even if the subscription plan technically
      // doesn't include it. Enforce the subscription gate only when no feature flag is
      // configured on the nav item, so plan-only items still respect their tier.
      if (item.featureFlag) {
        if (!checkFeatureFlag(item.featureFlag)) {
          return false
        }
      } else if (
        item.subscriptionFeature &&
        !checkFeature(item.subscriptionFeature)
      ) {
        return false
      }

      if (
        item.permission &&
        !checkPermission(item.permission.module, item.permission.action)
      ) {
        return false
      }

      return true
    })
  })()

  // Group the (already fully gated) nav items by rail area. Pinned collections
  // join the area their section maps to and are treated as regular nav items
  // so they share the same show/hide, "More" and muting logic as before.
  const areaNavItems = (() => {
    const map = new Map<RailAreaKey, NavItem[]>()
    const push = (area: RailAreaKey, item: NavItem) => {
      const list = map.get(area)
      if (list) {
        list.push(item)
      } else {
        map.set(area, [item])
      }
    }
    filteredNavItems.forEach(item => {
      push(getRailAreaForNavItem(item.href, item.section, { isPersonal }), item)
    })
    // Areas mixing items from multiple sections get an explicit read order.
    map.forEach((items, areaKey) => {
      const order = AREA_ITEM_ORDER[areaKey]
      if (order) {
        items.sort((a, b) => {
          const rank = (href: string) => {
            const index = order.indexOf(href)
            return index === -1 ? order.length : index
          }
          return rank(a.href) - rank(b.href)
        })
      }
    })
    accessiblePinnedCollections.forEach(pinned => {
      push(pinned.section ? SECTION_TO_AREA[pinned.section] : 'work', {
        href: `/collections/${pinned.slug}`,
        label: pinned.name,
        icon: getCollectionIcon(pinned.icon),
      })
    })
    return map
  })()

  // Areas render on the rail only when they have at least one item the user
  // can see — so flag/permission/subscription gating shapes the rail too.
  const visibleAreas = RAIL_AREAS.filter(
    area => (areaNavItems.get(area.key)?.length ?? 0) > 0,
  )

  // Roll the per-item unread counts up to the rail icons.
  const unreadByHref: Record<string, number | undefined> = {
    '/threads': unreadThreadsCount,
    '/tickets': unreadTicketsCount,
    '/live-chat': unreadLiveChatCount,
  }
  const areaUnreadCounts = (() => {
    const counts: Partial<Record<RailAreaKey, number>> = {}
    areaNavItems.forEach((items, areaKey) => {
      const total = items.reduce(
        (sum, item) => sum + (unreadByHref[item.href] ?? 0),
        0,
      )
      if (total > 0) {
        counts[areaKey] = total
      }
    })
    return counts
  })()

  // Which area the current route belongs to (drives the panel by default).
  const routeArea = (() => {
    for (const item of filteredNavItems) {
      if (isLinkActive(item.href)) {
        return getRailAreaForNavItem(item.href, item.section, { isPersonal })
      }
    }
    for (const pinned of accessiblePinnedCollections) {
      if (isLinkActive(`/collections/${pinned.slug}`)) {
        return pinned.section ? SECTION_TO_AREA[pinned.section] : 'work'
      }
    }
    return null
  })()

  const activeAreaKey = selectedArea ?? routeArea ?? 'home'
  const activeArea =
    visibleAreas.find(area => area.key === activeAreaKey) ?? visibleAreas[0]
  // The flown-out area on desktop (hover-driven).
  const hoverAreaDef =
    hoverArea && hoverArea !== 'pinned'
      ? visibleAreas.find(area => area.key === hoverArea)
      : undefined
  const isPinnedFlyout = hoverArea === 'pinned'

  // Personal accounts retitle some areas ("People & Home").
  const getAreaLabel = useCallback(
    (area: RailAreaDef) =>
      (isPersonal && area.personalLabel) || area.label,
    [isPersonal],
  )

  // Rail click. Desktop: the flyout is already open on hover, so a click
  // jumps straight to the area's first visible item. Mobile: switch the panel.
  const handleSelectArea = (areaKey: RailAreaKey) => {
    if (isMobile) {
      setSelectedArea(areaKey)
      return
    }
    const items = areaNavItems.get(areaKey) ?? []
    const target =
      items.find(
        item =>
          !isNavItemHidden(
            item.href,
            item.hidden || isSubscriptionNavItemLocked(item),
          ),
      ) ?? items[0]
    if (target) {
      navigate(getNavigationPath(target.href))
    }
  }

  // Dynamic text color classes based on theme
  const textClasses = useDarkText
    ? 'text-black/80 hover:text-black'
    : 'text-white/80 hover:text-white'
  const textMutedClasses = useDarkText ? 'text-black/60' : 'text-white/60'
  // Section labels use the .section-header utility for typography; its
  // muted-foreground color and bottom margin are overridden (via important
  // modifiers, since the utility compiles after core utilities) so the
  // theme-aware sidebar text colors still apply.
  const sectionLabelColorClasses = useDarkText
    ? '!text-black/60'
    : '!text-white/60'
  const hoverBgClasses = useDarkText ? 'hover:bg-black/10' : 'hover:bg-white/10'
  const activeBgClasses = useDarkText
    ? 'data-[active=true]:bg-black/15 data-[active=true]:text-black'
    : 'data-[active=true]:bg-white/15 data-[active=true]:text-white'
  const railActiveClasses = useDarkText
    ? 'bg-black/15 text-black'
    : 'bg-white/15 text-white'
  const borderClasses = useDarkText ? 'border-black/10' : 'border-white/5'

  // Single nav row renderer shared by the panel list and its "More" overflow.
  // options.pinned marks rows in the Pinned section: they get "Unpin" instead
  // of the show/hide toggle.
  const renderNavItem = (
    item: NavItem,
    isItemHidden = false,
    options?: { pinned?: boolean },
  ) => {
    const isPinnedRow = options?.pinned === true
    // Home-area rows are always visible at the top of the sidebar — pinning
    // them would just duplicate them, so the option is omitted.
    const isHomeItem =
      !!item.section &&
      getRailAreaForNavItem(item.href, item.section, { isPersonal }) === 'home'
    const isThreadsItem = item.href === '/threads'
    const isTicketsItem = item.href === '/tickets'
    const isLiveChatItem = item.href === '/live-chat'
    const subscriptionFeature = item.subscriptionFeature
    const unreadBadgeCount = isThreadsItem
      ? unreadThreadsCount
      : isTicketsItem
        ? unreadTicketsCount
        : isLiveChatItem
          ? unreadLiveChatCount
          : undefined
    const showUnreadBadge =
      typeof unreadBadgeCount === 'number' && unreadBadgeCount > 0
    const isSubscriptionLocked = isSubscriptionNavItemLocked(item)
    const subscriptionBadge = subscriptionFeature
      ? (SUBSCRIPTION_NAV_BADGES[subscriptionFeature] ?? 'Pro')
      : 'Pro'

    return (
      <SidebarMenuItem
        key={item.href}
        className="group/navitem flex justify-center"
      >
        <SidebarMenuButton
          asChild
          isActive={isLinkActive(item.href)}
          size="sm"
          className={cn(
            'h-7 px-2.5 text-left',
            textClasses,
            hoverBgClasses,
            activeBgClasses,
            isMobile && 'h-8 w-full px-4',
          )}
        >
          <Link
            to={getNavigationPath(item.href)}
            className="no-animate relative flex items-center gap-2"
            onClick={handleNavigate}
            onMouseEnter={() => handlePrefetch(item.href)}
            onFocus={() => handlePrefetch(item.href)}
          >
            {/* Area-accented indicator for the active row */}
            {isLinkActive(item.href) && activeArea && (
              <span
                aria-hidden
                className="absolute -left-1.5 top-1.5 bottom-1.5 w-[3px] rounded-full"
                style={{ backgroundColor: activeArea.accent }}
              />
            )}
            <div
              className={cn(
                'relative flex h-4 w-4 shrink-0 items-center justify-center',
                // Mute items that live under "More" so the split from
                // always-shown items reads at a glance — but keep the active
                // item at full strength.
                isItemHidden && !isLinkActive(item.href) && 'opacity-60',
              )}
            >
              <item.icon className="h-4 w-4" />
            </div>
            <span
              className={cn(
                'flex-1 truncate text-[13px]',
                isLinkActive(item.href) ? 'font-[550]' : 'font-[450]',
                isItemHidden && !isLinkActive(item.href) && 'opacity-60',
              )}
            >
              {item.label}
            </span>
            {isSubscriptionLocked && (
              <span className="rounded-full bg-amber-300/95 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-amber-950 shadow-sm ring-1 ring-black/10">
                {subscriptionBadge}
              </span>
            )}
            {showUnreadBadge && (
              <span
                className={cn(
                  'min-w-[18px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold',
                  'bg-blue-500 text-white',
                )}
              >
                {unreadBadgeCount > 99 ? '99+' : unreadBadgeCount}
              </span>
            )}
            {/* Row actions live behind a single "…" trigger so rows stay
                clean. It floats over the right edge (absolute, out of flow)
                and only appears on hover / focus / while its menu is open. */}
            <div className="absolute right-0.5 top-1/2 -translate-y-1/2">
              <DropdownMenu
                onOpenChange={open => {
                  rowMenuOpenRef.current = open
                  if (open) {
                    cancelFlyoutClose()
                  } else {
                    scheduleFlyoutClose()
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    aria-label={`${item.label} options`}
                    className={cn(
                      'bg-sidebar/80 focus-visible:ring-sidebar-ring grid h-6 w-6 place-items-center rounded-md p-0 opacity-0 backdrop-blur-sm transition-opacity',
                      'hover:bg-white/20 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1',
                      'group-hover/navitem:opacity-100 data-[state=open]:opacity-100',
                    )}
                  >
                    <DotsThreeIcon className="h-4 w-4" weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  side="right"
                  className="min-w-[7rem]"
                >
                  {showFocusWindowButton && (
                    <DropdownMenuItem
                      className="text-xs"
                      onClick={e =>
                        handleOpenFocusWindow(e, item.href, item.label)
                      }
                    >
                      <ArrowSquareOutIcon className="mr-2 h-3.5 w-3.5" />
                      Open in window
                    </DropdownMenuItem>
                  )}
                  {isPinnedRow ? (
                    <DropdownMenuItem
                      className="text-xs"
                      onClick={() => void unpinNavItem(item.href)}
                    >
                      <PushPinSlashIcon className="mr-2 h-3.5 w-3.5" />
                      Unpin
                    </DropdownMenuItem>
                  ) : (
                    <>
                      {isHomeItem ? null : isNavRoutePinned(item.href) ? (
                        <DropdownMenuItem
                          className="text-xs"
                          onClick={() => void unpinNavItem(item.href)}
                        >
                          <PushPinSlashIcon className="mr-2 h-3.5 w-3.5" />
                          Unpin
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          className="text-xs"
                          onClick={() =>
                            void pinNavItem({
                              href: item.href,
                              label: item.label,
                            })
                          }
                        >
                          <PushPinIcon className="mr-2 h-3.5 w-3.5" />
                          Pin
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-xs"
                        onClick={e =>
                          handleToggleNavVisibility(e, item.href, !isItemHidden)
                        }
                      >
                        {isItemHidden ? (
                          <EyeIcon className="mr-2 h-3.5 w-3.5" />
                        ) : (
                          <EyeSlashIcon className="mr-2 h-3.5 w-3.5" />
                        )}
                        {isItemHidden ? 'Show' : 'Hide'}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  // Panel contents for the active rail area, with the same visible/hidden
  // ("More") split and per-user overrides as the old section list.
  const renderAreaPanel = (area: RailAreaDef) => {
    const combinedItems = areaNavItems.get(area.key) ?? []

    // Split items into visible and hidden. A stored per-user override wins
    // over the item's built-in default so members can tailor their own sidebar.
    const visibleItems = combinedItems.filter(
      item =>
        !isNavItemHidden(
          item.href,
          item.hidden || isSubscriptionNavItemLocked(item),
        ),
    )
    const hiddenItems = combinedItems.filter(item =>
      isNavItemHidden(
        item.href,
        item.hidden || isSubscriptionNavItemLocked(item),
      ),
    )
    const isMoreExpanded = expandedMoreAreas.has(area.key)
    // Keep a hidden item visible inline while its page is active — but only
    // for items hidden by default. When the user has *explicitly* hidden an
    // item we honour that and let it move under "More" right away, so the
    // hide action gives immediate feedback.
    const activeHiddenItems = hiddenItems.filter(
      item =>
        isLinkActive(item.href) && effectiveHiddenNavItems[item.href] !== true,
    )
    const hasHiddenItems = hiddenItems.length > 0

    return (
      <div className="w-full max-w-full space-y-0.5 overflow-hidden">
        {visibleItems.map(item => renderNavItem(item, false))}
        {/* Show active hidden items inline (when not expanded) */}
        {!isMoreExpanded &&
          activeHiddenItems.map(item => renderNavItem(item, true))}
        {/* Expanded hidden items */}
        {isMoreExpanded && hiddenItems.map(item => renderNavItem(item, true))}
        {/* More toggle button */}
        {hasHiddenItems && (
          <SidebarMenuItem className="group/navitem flex justify-center">
            <SidebarMenuButton
              size="sm"
              className={cn(
                'h-6 px-2.5 text-left opacity-60 transition-opacity hover:opacity-100',
                textMutedClasses,
                hoverBgClasses,
                isMobile && 'w-full px-4',
              )}
              onClick={() => {
                setExpandedMoreAreas(prev => {
                  const next = new Set(prev)
                  if (next.has(area.key)) {
                    next.delete(area.key)
                  } else {
                    next.add(area.key)
                  }
                  return next
                })
              }}
            >
              <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                <DotsThreeIcon className="h-3.5 w-3.5" weight="bold" />
              </div>
              <span className="flex-1 text-xs font-[450]">
                {isMoreExpanded ? 'Less' : 'More'}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </div>
    )
  }

  // One icon on the rail per area, with rolled-up unread counts. Hovering
  // (or focusing) an icon flies the area's menu out; no tooltip needed since
  // the flyout header names the area.
  const renderRailArea = (area: RailAreaDef) => {
    const isAreaActive = activeArea?.key === area.key
    const isAreaFlownOut = hoverAreaDef?.key === area.key
    const unreadCount = areaUnreadCounts[area.key]
    const label = getAreaLabel(area)

    return (
      <button
        key={area.key}
        type="button"
        aria-label={label}
        aria-pressed={isAreaActive}
        onClick={() => handleSelectArea(area.key)}
        onMouseEnter={() => openFlyout(area.key)}
        onFocus={() => openFlyout(area.key)}
        className={cn(
          'relative grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
          isAreaActive
            ? useDarkText
              ? 'text-black'
              : 'text-white'
            : cn(
                textClasses,
                hoverBgClasses,
                isAreaFlownOut &&
                  (useDarkText ? 'bg-black/10' : 'bg-white/10'),
              ),
        )}
        // A translucent tint + hairline ring of the area accent keeps the
        // color-coding without fighting the two-tone icon strokes the way a
        // solid accent fill does.
        style={
          isAreaActive
            ? {
                backgroundColor: `${area.accent}30`,
                boxShadow: `inset 0 0 0 1px ${area.accent}59`,
              }
            : undefined
        }
      >
        <area.icon className="h-5 w-5" />
        {typeof unreadCount === 'number' && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[9px] font-bold leading-none text-white ring-1 ring-black/20">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    )
  }

  // Fixed bottom options, rendered twice: icon-only on the rail, and with
  // labels in the flyout's footer while the menu is expanded.
  const utilityItems: {
    key: string
    label: string
    icon: ReactNode
    isActive?: boolean
    onClick?: (e: MouseEvent<HTMLElement>) => void
    href?: string
    prefetchPath?: string
  }[] = [
    {
      key: 'help',
      label: 'Help Center',
      icon: <BookOpenTextIcon className="h-4 w-4" />,
      isActive: isLinkActive('/help'),
      onClick: handleOpenHelpCenter,
      prefetchPath: '/help',
    },
    // Workspace Settings — the unified settings hub. Open to every member:
    // org settings they can't manage render read-only. Explicit org path —
    // getNavigationPath treats /settings as the global appearance route.
    ...(currentOrganization?.slug
      ? [
          {
            key: 'settings',
            label: 'Workspace Settings',
            icon: <GearSixIcon className="h-4 w-4" />,
            isActive: isLinkActive('/settings'),
            href: `/${currentOrganization.slug}/settings`,
          },
        ]
      : []),
    ...(hasAdminPermissions
      ? [
          {
            key: 'admin',
            label: 'Admin Panel',
            icon: <GearIcon className="h-4 w-4" />,
            onClick: toggleAdminMode,
          },
        ]
      : []),
  ]

  // Pinned shortcuts entry on the rail, between Home and the areas. Hover
  // opens the Pinned flyout; click jumps to the first pin.
  const renderPinnedRailButton = () => (
    <button
      type="button"
      aria-label="Pinned"
      onClick={() => {
        const first = pinnedRows[0]
        if (first) {
          navigate(getNavigationPath(first.href))
        }
      }}
      onMouseEnter={() => openFlyout('pinned')}
      onFocus={() => openFlyout('pinned')}
      className={cn(
        'relative grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
        textClasses,
        hoverBgClasses,
        isPinnedFlyout && (useDarkText ? 'bg-black/10' : 'bg-white/10'),
      )}
    >
      <PushPinIcon className="h-5 w-5" />
    </button>
  )

  // Utility (Help / Settings / Admin) icons at the bottom of the rail.
  const renderRailUtility = ({
    key,
    label,
    icon,
    isActive,
    onClick,
    href,
    prefetchPath,
  }: {
    key: string
    label: string
    icon: ReactNode
    isActive?: boolean
    onClick?: (e: MouseEvent<HTMLElement>) => void
    href?: string
    prefetchPath?: string
  }) => {
    const sharedClassName = cn(
      'no-animate relative grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
      textClasses,
      hoverBgClasses,
      isActive && railActiveClasses,
    )
    const prefetchHandlers = prefetchPath
      ? {
          onMouseEnter: () => handlePrefetch(prefetchPath),
          onFocus: () => handlePrefetch(prefetchPath),
        }
      : {}

    return (
      <Tooltip key={key}>
        <TooltipTrigger asChild>
          {href ? (
            <Link
              to={href}
              aria-label={label}
              onClick={handleNavigate}
              className={sharedClassName}
              {...prefetchHandlers}
            >
              {icon}
            </Link>
          ) : (
            <button
              type="button"
              aria-label={label}
              onClick={onClick}
              className={sharedClassName}
              {...prefetchHandlers}
            >
              {icon}
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    )
  }

  // Labeled variant of the same options for the flyout footer.
  const renderFlyoutUtility = ({
    key,
    label,
    icon,
    isActive,
    onClick,
    href,
    prefetchPath,
  }: (typeof utilityItems)[number]) => {
    const sharedClassName = cn(
      // no-animate opts out of the global a:hover primary-color effect
      'no-animate flex h-7 w-full items-center gap-2 rounded-md px-2.5 text-[13px] font-[450] transition-colors',
      textClasses,
      hoverBgClasses,
      isActive && railActiveClasses,
    )
    const prefetchHandlers = prefetchPath
      ? {
          onMouseEnter: () => handlePrefetch(prefetchPath),
          onFocus: () => handlePrefetch(prefetchPath),
        }
      : {}

    return href ? (
      <Link
        key={key}
        to={href}
        onClick={handleNavigate}
        className={sharedClassName}
        {...prefetchHandlers}
      >
        {icon}
        <span className="truncate">{label}</span>
      </Link>
    ) : (
      <button
        key={key}
        type="button"
        onClick={onClick}
        className={sharedClassName}
        {...prefetchHandlers}
      >
        {icon}
        <span className="truncate">{label}</span>
      </button>
    )
  }

  // ---- Home Base style: pinned routes + collapsible area groups ----
  // Pinned = user-pinned page routes only. Pinned collections are modules,
  // so they stay inside their module's group (injected via areaNavItems).
  // Record pins carry visuals captured at pin time — an uploaded logo (e.g.
  // a client) or an initial swatch in the record's color (e.g. a project).
  // Other pins inherit their module's icon; ad-hoc pages fall back to the
  // pin glyph.
  const resolvePinnedIcon = (href: string) =>
    filteredNavItems.find(
      item => item.href === href || href.startsWith(`${item.href}/`),
    )?.icon ?? PushPinIcon
  const createPinnedVisualIcon = (
    pinned: PinnedNavRouteItem,
  ): NavItem['icon'] | null => {
    const { logoUrl, color, label } = pinned
    if (logoUrl) {
      return function PinnedLogoIcon({ className }: { className?: string }) {
        return (
          <img
            src={logoUrl}
            alt=""
            className={cn('rounded-[4px] object-cover', className)}
          />
        )
      }
    }
    if (color) {
      return function PinnedInitialIcon({
        className,
      }: {
        className?: string
      }) {
        return (
          <span
            className={cn(
              'flex items-center justify-center rounded-[4px] text-[9px] font-bold leading-none text-white',
              className,
            )}
            style={{ backgroundColor: color }}
          >
            {label.charAt(0).toUpperCase()}
          </span>
        )
      }
    }
    return null
  }
  const pinnedRows: NavItem[] = (navPreferences.pinnedNavItems ?? [])
    .map(pinned => ({
      href: pinned.href,
      label: pinned.label,
      icon: createPinnedVisualIcon(pinned) ?? resolvePinnedIcon(pinned.href),
    }))
    // Alphabetical, so the section stays scannable as pins accumulate.
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))

  const renderHomeBaseNav = () => {
    const homeArea = visibleAreas.find(area => area.key === 'home')
    const groupAreas = visibleAreas.filter(area => area.key !== 'home')

    return (
      <div>
        {homeArea && renderAreaPanel(homeArea)}

        {pinnedRows.length > 0 && (
          <div className="pt-4">
            <div
              className={cn(
                'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
                sectionLabelColorClasses,
              )}
            >
              Pinned
            </div>
            <div className="space-y-0.5">
              {pinnedRows.map(item =>
                renderNavItem(item, false, { pinned: true }),
              )}
            </div>
          </div>
        )}

        <div className="pt-4">
          <div
            className={cn(
              'mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
              sectionLabelColorClasses,
            )}
          >
            Workspace
          </div>
          <div className="space-y-0.5">
            {groupAreas.map(area => {
              // Default: collapsed, except the group holding the active
              // route. An explicit user toggle wins over both.
              const stored = collapsedNavGroups[area.key]
              const containsActive = routeArea === area.key
              const isGroupCollapsed = stored ?? !containsActive
              const unreadCount = areaUnreadCounts[area.key]
              const label = getAreaLabel(area)

              return (
                <div key={area.key}>
                  <button
                    type="button"
                    onClick={() =>
                      setNavGroupCollapsed(area.key, !isGroupCollapsed)
                    }
                    aria-expanded={!isGroupCollapsed}
                    className={cn(
                      'flex h-7 w-full items-center gap-2 rounded-md px-2.5 text-[13px] font-[500] transition-colors',
                      textClasses,
                      hoverBgClasses,
                    )}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      <area.icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 truncate text-left">{label}</span>
                    {/* Rolled-up unread signal while the group is closed */}
                    {isGroupCollapsed && typeof unreadCount === 'number' && (
                      <span className="min-w-[18px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-semibold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    <CaretDownIcon
                      className={cn(
                        'h-3 w-3 shrink-0 opacity-60 transition-transform',
                        isGroupCollapsed && '-rotate-90',
                      )}
                    />
                  </button>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-200 ease-in-out',
                      isGroupCollapsed
                        ? 'max-h-0 opacity-0'
                        : 'max-h-[2000px] opacity-100',
                    )}
                  >
                    {/* Plain indent like the mockup — no rule line */}
                    <div className="pl-5 pt-0.5">
                      {renderAreaPanel(area)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="app-shell-frame relative flex h-full min-w-0 flex-1"
      data-sidebar-collapsed={isSidebarCollapsed ? 'true' : 'false'}
      // Rail mode's footprint is the rail alone (see theme.css) — the area
      // menu flies out over the content on hover instead of occupying layout.
      // Admin mode and the Home Base style keep the full-width sidebar.
      data-rail-only={isRailMode ? 'true' : 'false'}
    >
      <Sidebar
        variant="sidebar"
        collapsible={useIsMobile() ? 'offcanvas' : 'icon'}
        tauriOffset={isTauri() && !isMobile}
        className={cn(
          'themed-sidebar enhanced-sidebar app-shell-sidebar bg-sidebar transition-colors duration-300',
          isDesktopTauri && 'tauri-desktop-sidebar',
          isDesktopTauri && tauriGlassEnabled && 'tauri-glass-sidebar',
          isMobile
            ? '!sm:p-0 !md:p-0 border-r-0 !p-0'
            : isDesktopTauri
              ? 'border-r-0'
              : 'border-r',
          borderClasses,
        )}
      >
        {/* Header section - Logo for web, close button for mobile */}
        {isMobile ? (
          <div className="bg-sidebar flex h-14 items-center justify-between px-4 transition-colors duration-300">
            <Link
              to="/organizations"
              className="hover:opacity-80"
              onClick={handleNavigate}
            >
              <span
                className={cn(
                  'font-kronaone text-lg font-normal tracking-wide',
                  useDarkText ? 'text-black/85' : 'text-white/85',
                )}
              >
                scrambl
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseMobile}
              className={cn('h-8 w-8', textClasses, hoverBgClasses)}
            >
              <XIcon className="h-5 w-5" weight="bold" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
        ) : (
          !isTauri() && (
            <div
              className={cn(
                'bg-sidebar flex h-14 items-center justify-center px-3 transition-colors duration-300',
              )}
            >
              <Link
                to="/organizations"
                className="hover:opacity-80"
                onClick={handleNavigate}
              >
                {/* Rail-only footprint fits the mark, not the wordmark; the
                    wide admin-mode and Home Base sidebars show the wordmark. */}
                {isRailMode || isSidebarCollapsed ? (
                  <img
                    src="/logo-outline.svg"
                    alt="Scrambl"
                    className="h-8 w-8"
                    style={{
                      filter: useDarkText
                        ? 'invert(1) opacity(0.8)'
                        : 'opacity(0.8)',
                    }}
                  />
                ) : (
                  <span
                    className={cn(
                      'font-kronaone text-lg font-normal tracking-wide',
                      useDarkText ? 'text-black/85' : 'text-white/85',
                    )}
                  >
                    scrambl
                  </span>
                )}
              </Link>
            </div>
          )
        )}

        <div className="relative flex min-h-0 flex-1">
          {/* Icon rail — one entry per area with visible modules. Hidden in
              admin mode and Home Base style, where the panel takes the full
              width. */}
          {!isAdminMode && !isHomeBase && (
            <div
              onMouseEnter={cancelFlyoutClose}
              onMouseLeave={scheduleFlyoutClose}
              className={cn(
                'flex w-12 flex-none flex-col items-center gap-1 overflow-y-auto py-2',
                'bg-sidebar transition-colors duration-300',
                // Mobile keeps the static panel next to the rail; give the
                // pair a divider. On desktop the rail is the whole sidebar.
                isMobile && cn('border-r', borderClasses),
              )}
            >
              {isNavLoading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <Skeleton
                    key={i}
                    className={cn(
                      'h-9 w-9 shrink-0 rounded-lg',
                      useDarkText ? 'bg-black/10' : 'bg-white/10',
                    )}
                  />
                ))
              ) : (
                <>
                  {visibleAreas
                    .filter(area => area.key === 'home')
                    .map(renderRailArea)}
                  {pinnedRows.length > 0 && renderPinnedRailButton()}
                  {visibleAreas
                    .filter(area => area.key !== 'home')
                    .map(renderRailArea)}
                </>
              )}
              <div className="min-h-4 flex-1" />
              {/* While the flyout is open its labeled footer stands in for
                  these — hide the icon set so the options don't double up. */}
              {!hoverAreaDef && utilityItems.map(renderRailUtility)}
            </div>
          )}

          {(isMobile || isAdminMode || isHomeBase) && (
          <SidebarContent
            className={cn(
              'bg-sidebar min-h-0 min-w-0 flex-1 transition-colors duration-300',
              isMobile ? '!p-0' : '',
            )}
          >
            <div className="sidebar-scrollbar h-full overflow-y-auto">
              <SidebarMenu
                className={cn(
                  'space-y-1',
                  // Adjust padding for mobile vs desktop
                  isMobile
                    ? 'px-3 pb-6 pt-2'
                    : 'pb-4 pt-1 group-data-[state=collapsed]:px-0 group-data-[state=expanded]:px-2',
                )}
              >
                {/* Back to App button for admin mode */}
                {isAdminMode && (
                  <div className={cn('mb-3 border-b pb-2', borderClasses)}>
                    <div className="space-y-0.5">
                      <SidebarMenuItem className="flex justify-center group-data-[state=collapsed]:mb-0">
                        <SidebarMenuButton
                          tooltip="Back to App"
                          size="sm"
                          onClick={toggleAdminMode}
                          className={cn(
                            'h-8 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                            textClasses,
                            hoverBgClasses,
                            isMobile && 'w-full px-4',
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <ArrowLeftIcon className="h-5 w-5" />
                            <span className="text-sm font-[450] group-data-[state=collapsed]:hidden">
                              Back to App
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                {isAdminMode ? (
                  <div>
                    <div className="hidden group-data-[state=expanded]:block group-data-[state=expanded]:px-1">
                      <h2
                        className={cn(
                          'section-header !mb-0 px-2.5 py-0.5',
                          sectionLabelColorClasses,
                        )}
                      >
                        Admin Panel
                      </h2>
                    </div>
                    <SidebarSeparator
                      className={cn(
                        'hidden opacity-10 group-data-[state=collapsed]:my-0.5',
                        useDarkText ? 'bg-black/5' : 'bg-white/3',
                      )}
                    />
                    <div className="space-y-0.5">
                      {adminNavItems
                        .filter(
                          item =>
                            !item.accountTypes ||
                            item.accountTypes.includes(accountType),
                        )
                        .map(item => (
                          <SidebarMenuItem
                            key={item.href}
                            className="flex justify-center group-data-[state=collapsed]:mb-0"
                          >
                            <SidebarMenuButton
                              asChild
                              isActive={isLinkActive(item.href)}
                              tooltip={item.label}
                              size="sm"
                              className={cn(
                                'h-8 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                                textClasses,
                                hoverBgClasses,
                                activeBgClasses,
                                isMobile && 'w-full px-4',
                              )}
                            >
                              <Link
                                to={getNavigationPath(item.href)}
                                className="flex items-center gap-2"
                                onClick={handleNavigate}
                                onMouseEnter={() => handlePrefetch(item.href)}
                                onFocus={() => handlePrefetch(item.href)}
                              >
                                <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                                  <item.icon className="h-5 w-5" />
                                </div>
                                <span
                                  className={cn(
                                    'text-sm group-data-[state=collapsed]:hidden',
                                    isLinkActive(item.href)
                                      ? 'font-[550]'
                                      : 'font-[450]',
                                  )}
                                >
                                  {item.label}
                                </span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                    </div>
                  </div>
                ) : isNavLoading ? (
                  // Loading skeleton for the panel
                  <div className="space-y-4">
                    <div>
                      <div className="mb-2 px-1">
                        <Skeleton
                          className={cn(
                            'h-4 w-16 rounded',
                            useDarkText ? 'bg-black/10' : 'bg-white/10',
                          )}
                        />
                      </div>
                      <div className="space-y-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div
                            key={i}
                            className="flex h-8 items-center gap-2 px-3"
                          >
                            <Skeleton
                              className={cn(
                                'h-5 w-5 rounded',
                                useDarkText ? 'bg-black/10' : 'bg-white/10',
                              )}
                            />
                            <Skeleton
                              className={cn(
                                'h-4 w-24 rounded',
                                useDarkText ? 'bg-black/10' : 'bg-white/10',
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : isHomeBase ? (
                  renderHomeBaseNav()
                ) : (
                  activeArea && (
                    <div>
                      {/* Panel header: the active area's name with its accent */}
                      <div className="mb-2 flex items-center gap-2 px-2.5 pt-1">
                        <span
                          aria-hidden
                          className="h-2 w-2 shrink-0 rounded-[3px]"
                          style={{ backgroundColor: activeArea.accent }}
                        />
                        <h2
                          className={cn(
                            'section-header !mb-0',
                            sectionLabelColorClasses,
                          )}
                        >
                          {getAreaLabel(activeArea)}
                        </h2>
                      </div>
                      {renderAreaPanel(activeArea)}
                    </div>
                  )
                )}
              </SidebarMenu>
            </div>
          </SidebarContent>
          )}
        </div>

        {/* Home Base footer: the fixed bottom options with labels */}
        {isHomeBase && !isAdminMode && (
          <div className={cn('space-y-0.5 border-t px-2 py-2', borderClasses)}>
            {utilityItems.map(renderFlyoutUtility)}
          </div>
        )}
      </Sidebar>
      {/* Desktop hover flyout: the hovered area's menu floats next to the
          rail, above the content, and hides when the pointer leaves the
          rail + flyout pair. Rendered at the frame level (not inside the
          Sidebar) because the sidebar container clips overflowing children. */}
      {isRailMode && (hoverAreaDef || isPinnedFlyout) && (
        <div
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
          className={cn(
            'themed-sidebar nav-flyout absolute bottom-0 left-12 z-50 flex w-56 flex-col overflow-hidden shadow-2xl',
          )}
          style={{
            top: isDesktopTauri ? 'var(--tauri-shell-offset, 52px)' : 0,
          }}
        >
          <div className="sidebar-scrollbar min-h-0 flex-1 overflow-y-auto">
            <SidebarMenu className="space-y-1 px-2 pb-4 pt-2">
              <div className="mb-2 flex items-center gap-2 px-2.5 pt-1">
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-[3px]"
                  style={{
                    backgroundColor: hoverAreaDef?.accent ?? '#F2B41C',
                  }}
                />
                <h2
                  className={cn(
                    'section-header !mb-0',
                    sectionLabelColorClasses,
                  )}
                >
                  {hoverAreaDef ? getAreaLabel(hoverAreaDef) : 'Pinned'}
                </h2>
              </div>
              {hoverAreaDef ? (
                renderAreaPanel(hoverAreaDef)
              ) : (
                <div className="w-full max-w-full space-y-0.5 overflow-hidden">
                  {pinnedRows.map(item =>
                    renderNavItem(item, false, { pinned: true }),
                  )}
                </div>
              )}
            </SidebarMenu>
          </div>
          {/* Labeled bottom options, aligned with the rail's fixed icons */}
          <div
            className={cn(
              'space-y-0.5 border-t px-2 py-2',
              borderClasses,
            )}
          >
            {utilityItems.map(renderFlyoutUtility)}
          </div>
        </div>
      )}
      <SidebarInset
        className={cn(
          'app-shell-inset flex h-full min-h-0 w-full min-w-0 flex-col',
          isDesktopTauri
            ? tauriGlassEnabled
              ? 'bg-transparent'
              : 'bg-background'
            : 'bg-background',
          !isMobile && 'mb-4 ml-0',
          !isMobile && !isDesktopTauri && 'shadow-sm',
        )}
        tauriOffset={isTauri() && !isMobile}
      >
        <Header />
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
          {isDesktopTauri && (
            <div
              aria-hidden
              className={cn(
                'app-shell-corner-notch pointer-events-none absolute left-0 top-0 z-0 h-4 w-4',
                tauriGlassEnabled ? 'tauri-glass-corner-fill' : 'bg-sidebar',
              )}
            />
          )}
          <div
            className={cn(
              'app-shell-content-surface relative z-10 h-full min-h-0 min-w-0 overflow-hidden rounded-tl-2xl',
            )}
          >
            {children}
          </div>
        </div>
      </SidebarInset>
    </div>
  )
}

export { CustomSidebarTrigger }
